/**
 * REV-17972 — service delivery match fields.
 *
 * `properties/service-delivery-match-fields` holds the list of attributes the
 * engine matches a PSD log against an order line on, for logs that arrive
 * without an Order Item Number. Supported values: productCode,
 * linkedProductCode, termDate.
 *
 * The setting is standalone: when it is set, logs are grouped by product code
 * and each is attached to the line these fields identify, the way
 * `properties/service-delivery-by-product-code` attaches them by product code.
 * When both are set, the match-fields setting decides matching.
 */

export const MATCH_FIELDS_CONFIG_ID =
    "properties/service-delivery-match-fields";

export const PSD_BY_PRODUCT_CODE_CONFIG_ID =
    "properties/service-delivery-by-product-code";

export const MatchField = {
    PRODUCT_CODE: "productCode",
    LINKED_PRODUCT_CODE: "linkedProductCode",
    TERM_DATE: "termDate"
} as const;

export type MatchFieldValue = (typeof MatchField)[keyof typeof MatchField];

export interface OrgConfigEntry {
    id: string;
    value?: unknown;
}

const ALLOWED_MATCH_FIELDS: readonly MatchFieldValue[] =
    Object.values(MatchField);

function isMatchField(field: unknown): field is MatchFieldValue {
    return ALLOWED_MATCH_FIELDS.includes(field as MatchFieldValue);
}

/**
 * Parse and validate a raw match-fields value.
 *
 * @returns null when the setting is unset, or a de-duplicated array of fields.
 * @throws when the value is an empty list or contains an unknown field, so a
 *         misconfigured tenant fails the sync up front rather than matching
 *         silently on nothing.
 */
export function parseMatchFields(value: unknown): MatchFieldValue[] | null {
    if (value == null) {
        return null;
    }

    if (!Array.isArray(value)) {
        throw new Error(
            `${MATCH_FIELDS_CONFIG_ID} must be a list. Allowed values: ` +
                `${ALLOWED_MATCH_FIELDS.join(", ")}.`
        );
    }

    if (value.length === 0) {
        throw new Error(
            `${MATCH_FIELDS_CONFIG_ID} is empty. Set at least one of ` +
                `${ALLOWED_MATCH_FIELDS.join(", ")}, or remove the setting.`
        );
    }

    const unknownFields = value.filter((field) => !isMatchField(field));
    if (unknownFields.length > 0) {
        throw new Error(
            `${MATCH_FIELDS_CONFIG_ID} has unknown value ` +
                `[${unknownFields.join(", ")}]. Allowed values: ` +
                `${ALLOWED_MATCH_FIELDS.join(", ")}.`
        );
    }

    return [...new Set(value.filter(isMatchField))];
}

/**
 * Read match-fields from an orgConfig array (the common `{ id, value }[]`
 * form). Returns null when unset.
 */
export function matchFieldsFromOrgConfig(
    orgConfig: readonly OrgConfigEntry[] | null | undefined
): MatchFieldValue[] | null {
    const config =
        orgConfig && orgConfig.find((c) => c.id === MATCH_FIELDS_CONFIG_ID);
    return parseMatchFields(config && config.value);
}

/**
 * True when PSD logs are matched to order lines by product code or by the
 * configured match fields, rather than naming their line by Order Item Number.
 * Logs are then stored grouped by product code. Set when match-fields is
 * set OR service-delivery-by-product-code is on; read this instead of
 * service-delivery-by-product-code wherever either mode applies.
 *
 * @throws on a bad match-fields value, see parseMatchFields.
 */
export function isPsdMatchByProductOrFieldsValues(
    matchFieldsValue: unknown,
    byProductCodeValue: unknown
): boolean {
    return !!parseMatchFields(matchFieldsValue) || !!byProductCodeValue;
}

/** isPsdMatchByProductOrFieldsValues, read from an orgConfig array. */
export function isPsdMatchByProductOrFields(
    orgConfig: readonly OrgConfigEntry[] | null | undefined
): boolean {
    const valueOf = (id: string): unknown => {
        const config = orgConfig && orgConfig.find((c) => c.id === id);
        return config && config.value;
    };
    return isPsdMatchByProductOrFieldsValues(
        valueOf(MATCH_FIELDS_CONFIG_ID),
        valueOf(PSD_BY_PRODUCT_CODE_CONFIG_ID)
    );
}
